import { makeAutoObservable } from 'mobx';

import CollectionPreviewStore from '@/collection/stores/CollectionPreviewStore';
import useStore from '@/common/hooks/useStore';
import Notifier from '@/common/services/Notifier';
import {
  DriveDestinationCommand,
  toCommandDriveItemId,
  toCommandItemsCount,
} from '@/drive/entities/DriveDestinationCommand';
import { DriveItemsResponse } from '@/drive/entities/DriveItemsResponse';
import DriveDestinationStore from '@/drive/stores/DriveDestinationStore';
import { DriveModifierStore } from '@/drive/stores/DriveModifierStore/DriveModifierStore';
import DriveModalTreeStore from '@/forwarding/components/ForwardModal/ForwardModalBody/DriveModalTree/DriveModalTreeStore';
import RecentDestinationsStore from '@/forwarding/components/ForwardModal/ForwardModalBody/RecentDestinations/RecentDestinationsStore';
import { ForwardForm } from '@/forwarding/types/ForwardForm';
import { EntityAccessEventEmitter } from '@/security/services/EntityAccessEventEmitter';

import { getDriveDestinationProductType } from './ForwardDrive.types';

class ForwardDriveStore {
  private driveDestinationStore: DriveDestinationStore;

  constructor(
    driveModifierStore: DriveModifierStore,
    private recentDestinationsStore: RecentDestinationsStore,
    private driveModalTreeStore: DriveModalTreeStore,
    collectionPreviewStore: CollectionPreviewStore,
    entityAccessEventEmitter: EntityAccessEventEmitter,
  ) {
    makeAutoObservable(this);

    this.driveDestinationStore = new DriveDestinationStore(
      driveModifierStore,
      collectionPreviewStore,
      entityAccessEventEmitter,
    );
  }

  get isSubmitting() {
    return this.driveDestinationStore.isSubmitting;
  }

  refresh = async (command: DriveDestinationCommand, isCollectionDestinationOnly = false) => {
    const forwardedItemId = toCommandDriveItemId(command);

    await Promise.all([
      this.recentDestinationsStore.refresh(command, isCollectionDestinationOnly, forwardedItemId),
      this.driveModalTreeStore.refresh(
        getDriveDestinationProductType(command),
        forwardedItemId,
        toCommandItemsCount(command),
      ),
    ]);
  };

  reset = () => {
    this.recentDestinationsStore.reset();
    this.driveModalTreeStore.reset();
  };

  addToRecentDestination = async (
    command: DriveDestinationCommand,
    form: ForwardForm,
    readOnly: boolean,
  ): Promise<Nullable<DriveItemsResponse>> => {
    const destination = this.recentDestinationsStore.selected;

    if (!destination) {
      Notifier.warn('Select Parent');

      return null;
    }

    return this.driveDestinationStore.executeCommand(command, form, destination, readOnly);
  };

  addToDriveTreeDestination = async (
    command: DriveDestinationCommand,
    form: ForwardForm,
    readOnly: boolean,
  ): Promise<Nullable<DriveItemsResponse>> => {
    let destination = this.driveModalTreeStore.selected;

    if (!destination) {
      Notifier.warn('Select Parent');

      return null;
    }

    if (this.driveModalTreeStore.newItem) {
      await this.driveModalTreeStore.finishCreation();
      destination = this.driveModalTreeStore.selected;
    }

    if (!destination) {
      return null;
    }

    return this.driveDestinationStore.executeCommand(command, form, destination, readOnly);
  };
}

export const useForwardDriveStore = () => useStore(ForwardDriveStore);

export default ForwardDriveStore;
